/**
 * TK-44 §5 (lee 24/10) — XẾP VỊ TRÍ ĐÈN trong Cài đặt ban đầu (`?xep=1`): luật THUẦN (không React, không trình duyệt) để bộ kiểm
 *   nạp thẳng. Bản nháp xếp (tên + vị trí) sống trên trang; "Loại bỏ" là CÙNG bản nháp với bảng node (một Set).
 */
export interface ToaDo { lat: number; lng: number }
/** Một node trong bảng (bảng đã lưu ∪ lượt dò) + thông tin xếp máy chủ trả. */
export interface NodeXep { addr: number; parentLuu: number | null; parentLuot: number | null; thayMs: number | null; ten: string | null; pos: ToaDo | null; gps: ToaDo | null }
/** Thay đổi chưa lưu của một đèn: `ten` (chuỗi rỗng = xoá tên) · `pos` (null = bỏ vị trí) · `tay` = tên do NGƯỜI gõ (TK-45 §5,
 *  Auto giữ nguyên; mất khi Lưu). Vắng khoá = không đổi. */
export interface NhapXep { ten?: string; pos?: ToaDo | null; tay?: boolean }

export const TEN_DEN_TOI_DA = 16;
export const tenSau = (h: NodeXep, n: NhapXep | undefined) => (n?.ten !== undefined ? n.ten.trim() || null : h.ten);
export const posSau = (h: NodeXep, n: NhapXep | undefined) => (n?.pos !== undefined ? n.pos : h.pos);

/** "Đã lưu" = thuộc bảng đã lưu VÀ không có thay đổi chờ (tên / vị trí / loại bỏ); còn lại "Chưa lưu" (gồm đèn mới). */
export function trangThaiXep(h: NodeXep, n: NhapXep | undefined, loai: ReadonlySet<number>): 'da-luu' | 'chua-luu' {
  const doi = !!n && ((n.ten !== undefined && (n.ten.trim() || null) !== h.ten) || n.pos !== undefined);
  return h.parentLuu !== null && !doi && !loai.has(h.addr) ? 'da-luu' : 'chua-luu';
}

/**
 * TK-45 §5 (lee 25/10): MẪU tên — `^(.*?)(\d+)(\D*)$` ⇒ tiền tố · số bắt đầu · độ rộng số (số chữ số của mẫu) · hậu tố.
 *   CS01 ⇒ "CS", 1, 2, "" · TRU001 ⇒ "TRU", 1, 3 · CS10 ⇒ bắt đầu 10. Không có số ⇒ null ("Mẫu phải có số").
 */
export interface MauTen { tienTo: string; batDau: number; rong: number; hauTo: string }
export function phanTichMau(mau: string): MauTen | null {
  const m = /^(.*?)(\d+)(\D*)$/.exec(mau.trim());
  return m ? { tienTo: m[1], batDau: Number(m[2]), rong: m[2].length, hauTo: m[3] } : null;
}
const theoMau = (m: MauTen, so: number) => `${m.tienTo}${String(so).padStart(m.rong, '0')}${m.hauTo}`;
/** Tên máy chủ tự đặt khi Lưu bảng ("Đèn N") — KHÔNG tính là sửa tay (Auto ghi đè được). */
const laTenMacDinh = (t: string) => /^Đèn \d+$/.test(t);
/** Số ĐẦU TIÊN ngay sau tiền tố (CS10-A ⇒ 10, CS10_B ⇒ 10); tên không theo tiền tố ⇒ null. */
function soSauTienTo(ten: string, tienTo: string): number | null {
  if (!ten.toLowerCase().startsWith(tienTo.toLowerCase())) return null;
  const m = /^\d+/.exec(ten.slice(tienTo.length));
  return m ? Number(m[0]) : null;
}

/**
 * AUTO đặt tên theo thứ tự dải thẻ. `giuTay` = true (nút "Auto"): tên ĐÃ SỬA TAY giữ nguyên và số kế tiếp nhảy theo số trong tên ấy;
 *   `giuTay` = false ("Auto lại tất cả"): bỏ hết tên cũ. Sửa tay = cờ `tay` của bản nháp; tên đã LƯU (không có nháp) khác tên Auto
 *   ở chỗ ấy và không phải "Đèn N" cũng coi là sửa tay. Trả bản nháp tên mới (chỉ đèn đổi tên). Tên được trùng.
 *   Ví dụ: mẫu CS01, 5 đèn, đèn 3 = CS10-A, đèn 4 = CS10_B ⇒ CS01 · CS02 · CS10-A · CS10_B · CS11.
 */
export function autoTen(ds: NodeXep[], nhap: ReadonlyMap<number, NhapXep>, m: MauTen, giuTay: boolean): Map<number, string> {
  const ra = new Map<number, string>();
  let ke = m.batDau;
  for (const h of ds) {
    const n = nhap.get(h.addr);
    const ten = tenSau(h, n);
    const tay = giuTay && !!ten && (n?.tay === true || (n?.ten === undefined && !laTenMacDinh(ten) && ten !== theoMau(m, ke)));
    if (tay) { const so = soSauTienTo(ten!, m.tienTo); if (so !== null) ke = so + 1; continue; }
    const moi = theoMau(m, ke++);
    if (moi !== ten) ra.set(h.addr, moi);
  }
  return ra;
}
/** Mẫu mặc định của ô Mẫu: tên đầu tiên đã có (theo dải thẻ, có số), không có thì "CS01". */
export function mauMacDinh(ds: NodeXep[], nhap: ReadonlyMap<number, NhapXep>): string {
  for (const h of ds) { const t = tenSau(h, nhap.get(h.addr)); if (t && !laTenMacDinh(t) && phanTichMau(t)) return t; }
  return 'CS01';
}

/** "Về tủ": khung ĐỐI XỨNG quanh tủ (mỗi đèn đã đặt + ảnh đối xứng của nó qua tủ) ⇒ ôm vừa tủ và các đèn mà TÂM vẫn là tủ. */
export function khungVeTu(tu: ToaDo, den: ToaDo[]): [number, number][] {
  const r: [number, number][] = [[tu.lat, tu.lng]];
  for (const p of den) { r.push([p.lat, p.lng]); r.push([2 * tu.lat - p.lat, 2 * tu.lng - p.lng]); }
  return r;
}

/** Gói gửi máy chủ (`PUT …/xep`): chỉ đèn có thay đổi; tên rỗng ⇒ null; vị trí bỏ ⇒ lat/lng null. */
export function goiLuuXep(nhap: ReadonlyMap<number, NhapXep>) {
  return [...nhap.entries()].map(([addr, n]) => ({
    addr,
    ...(n.ten !== undefined ? { name: n.ten.trim() || null } : {}),
    ...(n.pos !== undefined ? { lat: n.pos?.lat ?? null, lng: n.pos?.lng ?? null } : {}),
  }));
}
export const soViTri = (p: ToaDo | null) => (p ? `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}` : null);
